import type { ConditionOptionsHook, MultiSelectConditionComponentProps } from '@/types/advanceFilter';
import { MultiSelectConditionPanel } from './conditionComponents';
type ConditionComponent = (props: MultiSelectConditionComponentProps) => React.ReactNode;
interface MultiSelectConditionConfig {
  entityName: string;
  showSearchBox?: boolean;
  showAddNewButton?: boolean;
  useImagesInSelectedView?: boolean;
  /** For options whose name alone doesn't identify them — cloud accounts, where several can share a display name. */
  shouldShowId?: boolean;
}

/**
 * A multi-select condition whose panel chrome is fixed and whose options come
 * from `useOptions`. Lets a property declare its option source(s) without
 * repeating the panel wiring.
 */
export const multiSelectCondition = (config: MultiSelectConditionConfig, useOptions: ConditionOptionsHook): ConditionComponent => {
  const MultiSelectCondition = (props: MultiSelectConditionComponentProps) => {
    const {
      options,
      isLoading = false
    } = useOptions(props);
    return <MultiSelectConditionPanel showAddNewButton={config.showAddNewButton ?? false} showSearchBox={config.showSearchBox ?? false} useImagesInSelectedView={config.useImagesInSelectedView} shouldShowId={config.shouldShowId} isLoading={isLoading} entityName={config.entityName} options={options} {...props} />;
  };
  return MultiSelectCondition;
};

/**
 * Pairs a property's two option sources for the two surfaces that render its
 * condition: the filter strip gets `discovered` (values present in inventory),
 * every other surface gets `all`. Kept as two sibling components rather than a
 * branch inside one, so only the rendered variant runs its data-fetching hooks.
 */
const scopedCondition = (discovered: ConditionComponent, all: ConditionComponent): ConditionComponent => props => {
  const Variant = props.optionScope === 'discovered' ? discovered : all;
  return <Variant {...props} />;
};

/**
 * A {@link scopedCondition} whose two surfaces share the same panel chrome and
 * differ only in where their options come from.
 */
export const scopedMultiSelectCondition = (config: MultiSelectConditionConfig, useDiscoveredOptions: ConditionOptionsHook, useAllOptions: ConditionOptionsHook): ConditionComponent => scopedCondition(multiSelectCondition(config, useDiscoveredOptions), multiSelectCondition(config, useAllOptions));